import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Phone, Mail, Clock } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import PageSEO from "@/components/seo/PageSEO";
import { createLead, fetchServices } from "@/lib/api";
import { COMPANY } from "@/lib/company";

const BUDGET_OPTIONS = [
    "Under £100k",
    "£100k – £500k",
    "£500k – £2M",
    "£2M – £10M",
    "£10M+",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
    const [services, setServices] = useState([]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service_of_interest: "",
        project_details: "",
        budget_range: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        fetchServices().then(setServices).catch(() => setServices([]));
    }, []);

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((prev) => ({ ...prev, [k]: null }));
    };

    const validate = () => {
        const next = {};
        if (form.name.trim().length < 2) next.name = "Please enter your name.";
        if (!EMAIL_RE.test(form.email)) next.email = "Enter a valid email.";
        if (form.phone.trim().length < 5) next.phone = "Enter a phone number.";
        if (!form.service_of_interest) next.service_of_interest = "Choose a service.";
        if (form.project_details.trim().length < 10)
            next.project_details = "Give us at least a couple of sentences.";
        if (!form.budget_range) next.budget_range = "Pick an indicative budget.";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await createLead(form);
            setSubmitted(true);
            toast.success("Quote request received. We'll be in touch within one working day.");
            setForm({
                name: "",
                email: "",
                phone: "",
                service_of_interest: "",
                project_details: "",
                budget_range: "",
            });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const message = Array.isArray(detail)
                ? detail.map((d) => d.msg).join(", ")
                : (detail || "Something went wrong submitting your enquiry.");
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-brand-offwhite" data-testid="contact-page">
            <PageSEO
                title="Contact EB Construction — Manchester Construction Enquiries"
                description="Speak to EB Construction about your next project. 82 King Street, Manchester M2 4WQ. Response within one working day."
                canonicalPath="/contact"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    url: "https://elevatebuildconstruction.co.uk/contact",
                    name: "Contact EB Construction",
                    mainEntity: {
                        "@id": "https://elevatebuildconstruction.co.uk/#organization",
                    },
                }}
            />
            <section className="border-b border-brand-line py-24 md:py-32">
                <div className="container-x grid gap-16 md:grid-cols-12">
                    {/* Left: intro + contact info */}
                    <div className="md:col-span-5">
                        <Reveal>
                            <SectionLabel index={0} label="Contact" />
                            <h1 className="mt-6 font-display text-6xl leading-[1.05] md:text-7xl">
                                LET'S BUILD <br />
                                <span className="text-brand-gold">
                                    SOMETHING.
                                </span>
                            </h1>
                            <p className="mt-6 font-body text-lg leading-relaxed text-brand-steel">
                                Tell us about your project. A director will personally respond within one working day.
                            </p>
                        </Reveal>

                        <Reveal delay={0.15}>
                            <ul className="mt-12 flex flex-col gap-6 border-t border-brand-line pt-8 text-brand-charcoal">
                                <li className="flex items-start gap-4">
                                    <div className="grid h-10 w-10 flex-shrink-0 place-items-center bg-brand-charcoal text-brand-gold">
                                        <MapPin className="h-4 w-4" />
                                    </div>
                                    <div data-testid="contact-address">
                                        <p className="font-mono-tag text-brand-steel">
                                            Registered Office
                                        </p>
                                        <p className="mt-1 font-body">
                                            {COMPANY.address.line1}
                                            <br />
                                            {COMPANY.address.city}, {COMPANY.address.region}, {COMPANY.address.postcode}
                                        </p>
                                    </div>
                                </li>
                                {/* TODO: replace phone with real client-confirmed number when provided */}
                                <li className="flex items-start gap-4">
                                    <div className="grid h-10 w-10 flex-shrink-0 place-items-center bg-brand-charcoal text-brand-gold">
                                        <Phone className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <p className="font-mono-tag text-brand-steel">
                                            Phone
                                        </p>
                                        <a
                                            href={COMPANY.phoneHref}
                                            className="mt-1 block font-body hover:text-brand-gold"
                                            data-testid="contact-phone"
                                        >
                                            {COMPANY.phone}
                                        </a>
                                    </div>
                                </li>
                                {/* TODO: replace email with real client-confirmed inbox when provided */}
                                <li className="flex items-start gap-4">
                                    <div className="grid h-10 w-10 flex-shrink-0 place-items-center bg-brand-charcoal text-brand-gold">
                                        <Mail className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <p className="font-mono-tag text-brand-steel">
                                            Email
                                        </p>
                                        <a
                                            href={COMPANY.emailHref}
                                            className="mt-1 block font-body hover:text-brand-gold"
                                            data-testid="contact-email"
                                        >
                                            {COMPANY.email}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="grid h-10 w-10 flex-shrink-0 place-items-center bg-brand-charcoal text-brand-gold">
                                        <Clock className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <p className="font-mono-tag text-brand-steel">
                                            Hours
                                        </p>
                                        <p className="mt-1 font-body" data-testid="contact-hours">
                                            {COMPANY.hours}
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="grid h-10 w-10 flex-shrink-0 place-items-center bg-brand-charcoal text-brand-gold">
                                        <span className="font-mono-tag text-[9px] text-brand-offwhite">CO#</span>
                                    </div>
                                    <div>
                                        <p className="font-mono-tag text-brand-steel">
                                            Companies House
                                        </p>
                                        <p
                                            className="mt-1 font-body"
                                            data-testid="contact-company-number"
                                        >
                                            No. {COMPANY.companyNumber} · Registered in {COMPANY.jurisdiction}
                                        </p>
                                    </div>
                                </li>
                            </ul>
                        </Reveal>
                    </div>

                    {/* Right: form */}
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <form
                                onSubmit={submit}
                                noValidate
                                className="border border-brand-line bg-white p-8 md:p-12"
                                data-testid="lead-form"
                            >
                                <p className="font-mono-tag text-brand-gold">
                                    [ REQUEST A QUOTE ]
                                </p>
                                <h2 className="mt-3 font-display text-4xl leading-[1.1] md:text-5xl">
                                    PROJECT BRIEF
                                </h2>

                                <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
                                    <div>
                                        <label htmlFor="name" className="form-label">
                                            Full Name *
                                        </label>
                                        <input
                                            id="name"
                                            type="text"
                                            value={form.name}
                                            onChange={update("name")}
                                            className="form-input"
                                            data-testid="lead-form-name"
                                        />
                                        {errors.name && (
                                            <p className="mt-1 font-mono-tag text-destructive" data-testid="lead-form-name-error">
                                                {errors.name}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="form-label">
                                            Email *
                                        </label>
                                        <input
                                            id="email"
                                            type="email"
                                            value={form.email}
                                            onChange={update("email")}
                                            className="form-input"
                                            data-testid="lead-form-email"
                                        />
                                        {errors.email && (
                                            <p className="mt-1 font-mono-tag text-destructive" data-testid="lead-form-email-error">
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="phone" className="form-label">
                                            Phone *
                                        </label>
                                        <input
                                            id="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={update("phone")}
                                            className="form-input"
                                            data-testid="lead-form-phone"
                                        />
                                        {errors.phone && (
                                            <p className="mt-1 font-mono-tag text-destructive" data-testid="lead-form-phone-error">
                                                {errors.phone}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="service_of_interest"
                                            className="form-label"
                                        >
                                            Service of Interest *
                                        </label>
                                        <select
                                            id="service_of_interest"
                                            value={form.service_of_interest}
                                            onChange={update("service_of_interest")}
                                            className="form-input"
                                            data-testid="lead-form-service"
                                        >
                                            <option value="">— Select —</option>
                                            {services.map((s) => (
                                                <option key={s.id} value={s.title}>
                                                    {s.title}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.service_of_interest && (
                                            <p className="mt-1 font-mono-tag text-destructive" data-testid="lead-form-service-error">
                                                {errors.service_of_interest}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <label className="form-label">
                                        Indicative Budget *
                                    </label>
                                    <div
                                        className="mt-2 flex flex-wrap gap-2"
                                        data-testid="lead-form-budget-options"
                                    >
                                        {BUDGET_OPTIONS.map((b) => (
                                            <button
                                                type="button"
                                                key={b}
                                                onClick={() =>
                                                    setForm((f) => ({ ...f, budget_range: b }))
                                                }
                                                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                                    form.budget_range === b
                                                        ? "border-brand-gold bg-brand-gold text-brand-charcoal"
                                                        : "border-brand-line text-brand-charcoal hover:border-brand-charcoal"
                                                }`}
                                                data-testid={`lead-form-budget-${b.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                                            >
                                                {b}
                                            </button>
                                        ))}
                                    </div>
                                    {errors.budget_range && (
                                        <p className="mt-1 font-mono-tag text-destructive" data-testid="lead-form-budget-error">
                                            {errors.budget_range}
                                        </p>
                                    )}
                                </div>

                                <div className="mt-8">
                                    <label htmlFor="project_details" className="form-label">
                                        Project Details *
                                    </label>
                                    <textarea
                                        id="project_details"
                                        rows={5}
                                        value={form.project_details}
                                        onChange={update("project_details")}
                                        className="form-input resize-none"
                                        placeholder="Tell us about the site, scope, programme, and any known constraints."
                                        data-testid="lead-form-details"
                                    />
                                    {errors.project_details && (
                                        <p className="mt-1 font-mono-tag text-destructive" data-testid="lead-form-details-error">
                                            {errors.project_details}
                                        </p>
                                    )}
                                </div>

                                <div className="mt-10 flex items-center justify-between border-t border-brand-line pt-8">
                                    <p className="font-mono-tag text-brand-steel">
                                        {submitted ? "SUBMITTED — WE'LL BE IN TOUCH" : "* REQUIRED"}
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                                        data-testid="lead-form-submit"
                                    >
                                        {submitting ? "Sending…" : "Send Enquiry"}
                                        <ArrowUpRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
